import { useEffect, useMemo, useState } from "react";
import { searchPlaces, calculateDistance } from "../../../utils/googlePlaces";
import { normalizeAddress } from "./housePickParseAiResults";

const DEFAULT_MATCH_SCORING = {
  nameWeight: 0.68,
  proximityWeight: 0.1,
  addressWeight: 0.22,
  threshold: 0.58,
  minNameSimilarity: 0.3,
  minNameTokenIntersections: 1,
  minAddressSimilarity: 0.3,
  minAddressTokenIntersections: 2,
  maxDistanceFromAiKm: 3.21868,
  useNameWeight: false,
  useProximityWeight: false,
  useAddressWeight: false,
  useScoreThreshold: false,
  useMinNameSimilarity: true,
  useMinNameTokenIntersections: false,
  useMinAddressSimilarity: true,
  useMinAddressTokenIntersections: false,
  useMaxDistanceFromAiKm: true,
  useStreetNumberMismatchCheck: false,
  useOneSidedStreetNumberCheck: false,
};

function clampNumber(value, min, max, fallback) {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, n));
}

function getEffectiveMatchScoring(config) {
  const useNameWeight = config?.useNameWeight !== false;
  const useProximityWeight = config?.useProximityWeight !== false;
  const useAddressWeight = config?.useAddressWeight !== false;

  const rawName = useNameWeight
    ? clampNumber(config?.nameWeight, 0, 1, DEFAULT_MATCH_SCORING.nameWeight)
    : 0;
  const rawProx = useProximityWeight
    ? clampNumber(config?.proximityWeight, 0, 1, DEFAULT_MATCH_SCORING.proximityWeight)
    : 0;
  const rawAddr = useAddressWeight
    ? clampNumber(config?.addressWeight, 0, 1, DEFAULT_MATCH_SCORING.addressWeight)
    : 0;
  const total = rawName + rawProx + rawAddr;
  const normalized =
    total > 0
      ? { nameWeight: rawName / total, proximityWeight: rawProx / total, addressWeight: rawAddr / total }
      : { nameWeight: 1 / 3, proximityWeight: 1 / 3, addressWeight: 1 / 3 };
  return {
    useNameWeight,
    useProximityWeight,
    useAddressWeight,
    useScoreThreshold: config?.useScoreThreshold !== false,
    useMinNameSimilarity: config?.useMinNameSimilarity !== false,
    useMinNameTokenIntersections: config?.useMinNameTokenIntersections !== false,
    useMinAddressSimilarity: config?.useMinAddressSimilarity !== false,
    useMinAddressTokenIntersections: config?.useMinAddressTokenIntersections !== false,
    useMaxDistanceFromAiKm: config?.useMaxDistanceFromAiKm !== false,
    useStreetNumberMismatchCheck: config?.useStreetNumberMismatchCheck !== false,
    useOneSidedStreetNumberCheck: config?.useOneSidedStreetNumberCheck !== false,
    nameWeight: normalized.nameWeight,
    proximityWeight: normalized.proximityWeight,
    addressWeight: normalized.addressWeight,
    threshold: clampNumber(config?.threshold, 0.3, 0.95, DEFAULT_MATCH_SCORING.threshold),
    minNameSimilarity: clampNumber(
      config?.minNameSimilarity,
      0.01,
      0.95,
      DEFAULT_MATCH_SCORING.minNameSimilarity
    ),
    minNameTokenIntersections: Math.round(
      clampNumber(
        config?.minNameTokenIntersections,
        1,
        8,
        DEFAULT_MATCH_SCORING.minNameTokenIntersections
      )
    ),
    minAddressSimilarity: clampNumber(
      config?.minAddressSimilarity,
      0.2,
      0.95,
      DEFAULT_MATCH_SCORING.minAddressSimilarity
    ),
    minAddressTokenIntersections: Math.round(
      clampNumber(
        config?.minAddressTokenIntersections,
        1,
        8,
        DEFAULT_MATCH_SCORING.minAddressTokenIntersections
      )
    ),
    maxDistanceFromAiKm: clampNumber(
      config?.maxDistanceFromAiKm,
      0.2,
      50,
      DEFAULT_MATCH_SCORING.maxDistanceFromAiKm
    ),
  };
}

function tokenizeName(s) {
  return new Set(
    String(s || "")
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, " ")
      .split(/\s+/)
      .filter((w) => w.length > 2)
  );
}

function normalizeText(s) {
  return String(s || "")
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function tokenizeAddress(s) {
  return new Set(
    normalizeText(s)
      .split(" ")
      .filter((w) => w.length > 2)
  );
}

function extractStreetNumber(s) {
  const m = normalizeText(s).match(/\b\d{1,6}[a-z]?\b/);
  return m ? m[0] : "";
}

function nameSimilarity(aiName, placeName) {
  const na = normalizeText(aiName);
  const nb = normalizeText(placeName);
  if (!na || !nb) return 0;
  if (na === nb) return 1;
  if (nb.includes(na) || na.includes(nb)) return 0.88;
  const ta = tokenizeName(na);
  const tb = tokenizeName(nb);
  if (ta.size === 0 || tb.size === 0) return 0;
  let inter = 0;
  for (const w of ta) {
    if (tb.has(w)) inter += 1;
  }
  return inter / Math.max(ta.size, tb.size);
}

function nameIntersectionCount(a, b) {
  const ta = tokenizeName(a);
  const tb = tokenizeName(b);
  let inter = 0;
  for (const t of ta) {
    if (tb.has(t)) inter += 1;
  }
  return inter;
}

function addressSimilarity(a, b) {
  const ta = tokenizeAddress(a);
  const tb = tokenizeAddress(b);
  if (!ta.size || !tb.size) return 0;
  let inter = 0;
  for (const t of ta) {
    if (tb.has(t)) inter += 1;
  }
  return inter / Math.max(ta.size, tb.size);
}

function addressIntersectionCount(a, b) {
  const ta = tokenizeAddress(a);
  const tb = tokenizeAddress(b);
  let inter = 0;
  for (const t of ta) {
    if (tb.has(t)) inter += 1;
  }
  return inter;
}

function proximityFromAiSuggestion(km) {
  if (km == null || Number.isNaN(km)) return 0.5;
  if (km < 0.25) return 1;
  if (km < 1) return 0.85;
  if (km < 3) return 0.65;
  if (km < 8) return 0.4;
  return 0.2;
}

/**
 * Resolve a House Pick row to a Google Places (Text Search) result when possible.
 * @param {object} result — parsed House Pick item (name, address, coordinates)
 * @param {number} [userLatitude]
 * @param {number} [userLongitude]
 * @returns {Promise<object|null>} Processed place from searchPlaces, or null
 */
export async function matchHousePickResultToGooglePlace(
  result,
  userLatitude,
  userLongitude,
  scoringConfig = DEFAULT_MATCH_SCORING
) {
  const scoring = getEffectiveMatchScoring(scoringConfig);
  const name = typeof result?.name === "string" ? result.name.trim() : "";
  if (!name) return null;

  const addressLine = normalizeAddress(result?.address);
  const textQuery = [name, addressLine].filter(Boolean).join(" ").slice(0, 280);

  const lat =
    typeof userLatitude === "number" && Number.isFinite(userLatitude) ? userLatitude : undefined;
  const lng =
    typeof userLongitude === "number" && Number.isFinite(userLongitude) ? userLongitude : undefined;

  const locationBias =
    lat != null && lng != null ? { latitude: lat, longitude: lng } : undefined;

  let places;
  try {
    places = await searchPlaces(textQuery, {
      locationBias,
      maxResultCount: 8,
      rankPreference: "RELEVANCE",
      skipQueryEnhancement: true,
    });
  } catch {
    return null;
  }

  if (!places.length) return null;

  const aiLat = result?.coordinates?.latitude;
  const aiLng = result?.coordinates?.longitude;
  const hasAiCoords =
    typeof aiLat === "number" &&
    Number.isFinite(aiLat) &&
    typeof aiLng === "number" &&
    Number.isFinite(aiLng);

  let best = null;
  let bestScore = -1;

  for (const place of places) {
    const ns = nameSimilarity(name, place.name);
    const as = addressSimilarity(addressLine, place.address);
    const addressIntersections = addressIntersectionCount(addressLine, place.address);
    let kmToAi = null;
    if (hasAiCoords && place.latitude && place.longitude) {
      kmToAi = calculateDistance(aiLat, aiLng, place.latitude, place.longitude);
    }
    const prox = hasAiCoords ? proximityFromAiSuggestion(kmToAi) : 0.55;
    const score =
      ns * scoring.nameWeight +
      prox * scoring.proximityWeight +
      as * scoring.addressWeight;

    const nameIntersections = nameIntersectionCount(name, place.name);
    const gpStreet = extractStreetNumber(addressLine);
    const placeStreet = extractStreetNumber(place.address);
    const hasStreetMismatch = !!gpStreet && !!placeStreet && gpStreet !== placeStreet;
    const bothHaveAddressText = !!addressLine && !!place.address;
    const oneSidedStreetNumber = bothHaveAddressText && (!!gpStreet !== !!placeStreet);
    const addrSimOk =
      !scoring.useMinAddressSimilarity || as >= scoring.minAddressSimilarity;
    const addrTokOk =
      !scoring.useMinAddressTokenIntersections ||
      addressIntersections >= scoring.minAddressTokenIntersections;
    const strongAddressMatch =
      !bothHaveAddressText || (addrSimOk && addrTokOk);
    const nameTokOk =
      !scoring.useMinNameTokenIntersections ||
      nameIntersections >= scoring.minNameTokenIntersections;
    const nameSimOk = !scoring.useMinNameSimilarity || ns >= scoring.minNameSimilarity;
    const strictPass =
      nameTokOk &&
      nameSimOk &&
      (!scoring.useMaxDistanceFromAiKm ||
        !hasAiCoords ||
        kmToAi == null ||
        kmToAi <= scoring.maxDistanceFromAiKm) &&
      (!scoring.useStreetNumberMismatchCheck || !hasStreetMismatch) &&
      (!scoring.useOneSidedStreetNumberCheck || !oneSidedStreetNumber) &&
      strongAddressMatch;

    if (strictPass && score > bestScore) {
      bestScore = score;
      best = place;
    }
  }

  if (!best) return null;
  if (scoring.useScoreThreshold && bestScore < scoring.threshold) return null;

  return best;
}

/**
 * When House Pick results are shown, text-search Google Places per row (biased to user location).
 * @returns {Array<{ status: 'idle' | 'loading' | 'matched' | 'none' | 'error', place?: object }>}
 */
export function useHousePickGooglePlaceMatches(
  parsedResult,
  userLatitude,
  userLongitude,
  scoringConfig = DEFAULT_MATCH_SCORING
) {
  const [matches, setMatches] = useState([]);
  const scoring = useMemo(() => getEffectiveMatchScoring(scoringConfig), [scoringConfig]);

  useEffect(() => {
    if (!Array.isArray(parsedResult) || parsedResult.length === 0) {
      setMatches([]);
      return;
    }

    let active = true;
    setMatches(parsedResult.map(() => ({ status: "loading" })));

    (async () => {
      const next = await Promise.all(
        parsedResult.map(async (row) => {
          try {
            const place = await matchHousePickResultToGooglePlace(row, userLatitude, userLongitude, scoring);
            return place ? { status: "matched", place } : { status: "none" };
          } catch {
            return { status: "error" };
          }
        })
      );
      if (!active) return;
      setMatches(next);
    })();

    return () => {
      active = false;
    };
  }, [parsedResult, userLatitude, userLongitude, scoring]);

  return matches;
}
